import { isServiceWorkerAwake, SwWakeResult } from 'shared/channels/ensureServiceWorkerAwake';
import { ServiceWorkerUnavailable } from './requestServiceWorkerHealthReport';
import { describeDatabaseSync, listEntitySyncProblems } from 'shared/domain/serviceData/entitySync';
import type { ServiceWorkerHealthReport } from 'shared/domain/messages/serviceWorkerHealth';
import { LogLevel } from 'shared/types/logger';
import type { DiagnosticsStepFailure, ServiceWorkerDiagnostics } from './types';

function isFailedStep(value: unknown): value is DiagnosticsStepFailure {
  return typeof value === 'object' && value !== null && 'failed' in value;
}

/** Why the worker did not answer, as far as the diagnostics tell; groups the Sentry issues. */
export enum ServiceWorkerFailureKind {
  noWorker = 'no-worker',
  /** The worker runs an older version than the page and ignores its new messages. */
  outdatedWorker = 'outdated-worker',
  workerUnresponsive = 'worker-unresponsive',
  /** The worker answers the ping with the page's version but did not report its health in time: busy, not outdated. */
  workerBusy = 'worker-busy',
  /** An entity waits for a download that does not finish, or is empty after one. */
  entitySyncProblem = 'entity-sync-problem',
  /** The worker confirmed the request but never answered it. */
  receivedWithoutResponse = 'received-without-response',
  /** The worker answers now: the failure was transient (e.g. a stopped worker). */
  workerHealthy = 'worker-healthy',
}

/**
 * One cause shows as this kind for every request that waited (a sync that
 * cannot finish, e.g. without network): it is logged once per page load,
 * not once per channel.
 */
export function isFailureKindLoggedOnce(kind: ServiceWorkerFailureKind): boolean {
  return kind === ServiceWorkerFailureKind.entitySyncProblem;
}

/** The worker answers again: the request was slow or lost while it started. */
export function isWorkerHealthyFailure(kind: ServiceWorkerFailureKind): boolean {
  return kind === ServiceWorkerFailureKind.workerHealthy;
}

/** The worker runs an older version than the page: it is asked to update. */
export function isOutdatedWorkerFailure(kind: ServiceWorkerFailureKind): boolean {
  return kind === ServiceWorkerFailureKind.outdatedWorker;
}

/** A worker that answers again, or a page without network, is no failure of the app: info, not error. */
export function serviceWorkerFailureLogLevel(kind: ServiceWorkerFailureKind, isPageOnline: boolean): LogLevel {
  return isWorkerHealthyFailure(kind) || !isPageOnline ? LogLevel.INFO : LogLevel.ERROR;
}

/**
 * The registration was read and holds no worker at all: nothing controls the
 * page, nothing is installing or waiting. The wake-up may time out waiting for
 * one that never comes, but the worker is missing, not unresponsive.
 */
function hasNoWorker(registration: ServiceWorkerDiagnostics['registration']): boolean {
  if (registration === null || isFailedStep(registration)) {
    return false;
  }
  const { hasController, active, waiting, installing } = registration;
  return !hasController && active === null && waiting === null && installing === null;
}

function isHealthReport(health: ServiceWorkerDiagnostics['health']): health is ServiceWorkerHealthReport {
  return typeof health === 'object' && health !== null && 'swVersion' in health;
}

/**
 * The worker did not report its health in time, or at all: the wake-up ping
 * (and its pong with the version) tells which of the causes it is.
 */
function classifyWorkerWithoutHealthReport({
  health,
  wakeResult,
  pong,
  pageVersion,
  registration,
}: Pick<
  ServiceWorkerDiagnostics,
  'health' | 'wakeResult' | 'pong' | 'pageVersion' | 'registration'
>): ServiceWorkerFailureKind {
  if (
    health === ServiceWorkerUnavailable.noWorker ||
    wakeResult === SwWakeResult.noWorker ||
    hasNoWorker(registration)
  ) {
    return ServiceWorkerFailureKind.noWorker;
  }
  if (!isServiceWorkerAwake(wakeResult)) {
    return ServiceWorkerFailureKind.workerUnresponsive;
  }
  // Every worker answers the ping, and its pong tells its version. One that
  // sends none is older than the pong details, and so than the health check.
  return pong && pong.swVersion === pageVersion
    ? ServiceWorkerFailureKind.workerBusy
    : ServiceWorkerFailureKind.outdatedWorker;
}

/**
 * `acknowledged` comes from the failed request: `null` when unknown (no
 * request behind the failure, e.g. a spinner), `false` when the worker never
 * confirmed receiving it.
 */
export function classifyServiceWorkerFailure(
  diagnostics: ServiceWorkerDiagnostics,
  acknowledged: boolean | null = null
): ServiceWorkerFailureKind {
  const { health, database, pageVersion } = diagnostics;
  if (!isHealthReport(health)) {
    return classifyWorkerWithoutHealthReport(diagnostics);
  }
  if (health.swVersion !== pageVersion) {
    return ServiceWorkerFailureKind.outdatedWorker;
  }
  if (!isFailedStep(database) && listEntitySyncProblems(database).length > 0) {
    return ServiceWorkerFailureKind.entitySyncProblem;
  }
  if (acknowledged === true) {
    return ServiceWorkerFailureKind.receivedWithoutResponse;
  }
  return ServiceWorkerFailureKind.workerHealthy;
}

/**
 * Flattens the diagnostics to two levels: Sentry cuts extras deeper than
 * three levels (`normalizeDepth`), which would hide the per-entity state.
 */
export function toServiceWorkerDiagnosticsLog({
  pageVersion,
  wakeResult,
  registration,
  health,
  pong,
  database,
}: ServiceWorkerDiagnostics): Record<string, unknown> {
  const databaseLog = isFailedStep(database)
    ? { databaseReadFailed: database.failed }
    : {
        entitySyncProblems: listEntitySyncProblems(database),
        entitySync: describeDatabaseSync(database),
      };
  return {
    pageVersion,
    swWakeResult: wakeResult,
    swRegistration: registration,
    swHealth: health,
    ...(pong && { swPongVersion: pong.swVersion }),
    ...databaseLog,
  };
}
